import { useEffect, useState } from 'react';

/**
 * Which section the reader is currently looking at, for the TOC's
 * active state. Same rules as the handwritten page:
 *
 * `IntersectionObserver` against the viewport (`root: null`);
 * `rootMargin` narrows the band to a strip near the top; active is the
 * LAST intersecting section in document order; an empty strip keeps
 * the previous value so the highlight never blinks off.
 *
 * Feature-detected: happy-dom has no IntersectionObserver.
 */
export function useActiveSection(sectionIds: readonly string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  const idsKey = sectionIds.join('\0');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const ids = idsKey.length === 0 ? [] : idsKey.split('\0');
    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) intersecting.add(entry.target.id);
          else intersecting.delete(entry.target.id);
        }
        let current: string | null = null;
        for (const id of ids) {
          if (intersecting.has(id)) current = id;
        }
        if (current !== null) setActive(current);
      },
      { rootMargin: '-72px 0px -68% 0px', threshold: 0 },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el !== null) observer.observe(el);
    }
    return () => {
      observer.disconnect();
    };
  }, [idsKey]);

  return active;
}
